/* eslint-disable @typescript-eslint/no-explicit-any */
import { TableCaption, TableCell, TableHeader } from '@contensis/canvas-react'
import { useEffect, useMemo, useRef, useState } from 'react'
import MonoNumberWrapper from '~/components/Content/Tables/MonoNumberWrapper'
import { cn } from '~/lib/utils'
import { containsNumber } from '~/utils/misc'

type TablesProps = {
  block: any
  layoutColour?: string
}

const TableContent = (props: TablesProps) => {
  const { layoutColour } = props
  const block = props.block?.value

  const iconColour =
    layoutColour === 'ink-blue' || layoutColour === 'mid-blue'
      ? 'single-colour/white'
      : 'duotone/ink-blue/mid-blue'

  const [isOverflow, setIsOverflow] = useState(false)
  const containerRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const checkOverflow = () => {
      // Check if the containerRef is available
      if (containerRef.current) {
        setIsOverflow(
          // Check if the scrollWidth is greater than the clientWidth
          containerRef.current.scrollWidth > containerRef.current.clientWidth
        )
      }
    }

    checkOverflow()
    // Add event listener to check for overflow on resize
    window.addEventListener('resize', checkOverflow)

    return () => {
      // Remove event listener on unmount
      window.removeEventListener('resize', checkOverflow)
    }
  }, [])

  const tableContent = useMemo(() => {
    const renderRow = (row: any, rowIndex: number, isHeader = false) => (
      <tr
        key={row.id || rowIndex}
        className={`${
          isHeader
            ? '*:border-blue-l *:border-r-2 *:last:border-none'
            : 'even:bg-blue-l-20 odd:bg-blue-l-40 *:border-blue-l *:px-s *:py-xs *:border-r-2 *:last:border-none'
        }`}
      >
        {row.value.map((cell: any, cellIndex: number) =>
          isHeader ? (
            <th
              key={cell.id || cellIndex}
              className='px-s text-base-sm sm:text-base-md lg:text-base-lg py-xs min-w-[200px]'
            >
              <TableHeader.Children block={cell} />
            </th>
          ) : (
            <td key={cell.id || cellIndex}>
              {containsNumber(cell.value) ? (
                <MonoNumberWrapper block={cell} />
              ) : (
                <TableCell.Children block={cell} />
              )}
            </td>
          )
        )}
      </tr>
    )

    return (
      Array.isArray(block) &&
      block.map((item: any, index: number) => {
        switch (item.type) {
          case '_tableCaption':
            return (
              <caption
                key={index}
                className='sr-only caption-top dark:text-white'
              >
                <TableCaption.Children block={item} />
              </caption>
            )
          case '_tableHeader':
            return (
              <thead
                key={index}
                className={cn(
                  ['ink-blue', 'mid-blue'].includes(layoutColour ?? '')
                    ? 'text-blue-d bg-white'
                    : 'bg-blue-m dark:text-blue-d text-white dark:bg-white'
                )}
              >
                {item.value.map((row: any, rowIndex: number) =>
                  renderRow(row, rowIndex, true)
                )}
              </thead>
            )
          case '_tableBody':
            return (
              <tbody key={index}>
                {item.value.map((row: any, rowIndex: number) =>
                  renderRow(row, rowIndex)
                )}
              </tbody>
            )
          default:
            return null
        }
      })
    )
  }, [block])

  return (
    <div className='relative overflow-x-auto' ref={containerRef}>
      {isOverflow && (
        <div className='pb-xs gap-2xs-plus sm:gap-xs lg:gap-s sticky left-0 flex items-center'>
          <img
            src={`https://preview-websitemodern-uni-hull.cloud.contensis.com/site-elements/icons/${iconColour}/swipe-left.svg`}
            alt=''
            className='size-s md:size-m'
            role='presentation'
          />
          <p className='tag'>Swipe to see more</p>
        </div>
      )}
      <table
        className={cn(
          isOverflow ? 'pb-xs-plus' : '',
          'dark:text-blue-d text-blue-d relative max-h-[400px] w-full table-auto border-separate border-spacing-0'
        )}
      >
        {tableContent}
      </table>
    </div>
  )
}

export default TableContent
